<template>
  <ResponsiveLayout
    sticky-actions
    :progress="progress"
    :show-lang-picker="false"
  >
    <template #header_start>
      <ButtonBack @click="emit('back')" />
    </template>
    <template #header_middle>
      <div class="kds-typography-title-medium">
        {{ $t('b2b_self_test_generic_assessment') }}
      </div>
    </template>
    <div class="kds-main-wrapper">
      <h3
        class="kds-typography-title-xlarge"
        v-html="$t('b2b_self_test_pain_duration_question')"
      />
      <div class="self-test">
        <fieldset class="form-container">
          <div
            v-for="chronificationLevel in chronificationLevels"
            :key="chronificationLevel.value"
            :class="[
              'quiz-radio',
              { 'quiz-radio--checked': chronificationLevel.checked },
            ]"
            @click="handleChronificationLevelChange(chronificationLevel)"
          >
            <label class="quiz-radio__label kds-typography-title-medium">
              {{ $t(chronificationLevel.label) }}
            </label>
            <div class="quiz-radio__layout">
              <custom-radio
                :checked="chronificationLevel.checked"
                :data-q-a="'pain-duration-' + chronificationLevel.value"
                :value="chronificationLevel.value"
              />
            </div>
          </div>
        </fieldset>
      </div>
    </div>
    <template #actions>
      <BaseButton
        :disabled="!isValid"
        :text="$t('generic_action_continue')"
        data-qa="submit"
        @click="handleNextEvent"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import useSelfTestStore from '@shared/modules/assessment/stores/useSelfTestStore.js'
import CustomRadio from '@shared/components/CustomRadio.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import env from '@shared/env'
import { computed } from 'vue'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import ButtonBack from '@shared/components/ButtonBack.vue'

const getDefaultChronificationLevels = () =>
  env.isMskEu
    ? [
        {
          value: 'less-than-6-weeks',
          label: 'b2b_self_test_pain_duration_option_below_6_weeks',
          checked: false,
        },
        {
          value: '6-to-12-weeks',
          label: 'b2b_self_test_pain_duration_option_6_to_12_weeks',
          checked: false,
        },
        {
          value: '12-to-24-weeks',
          label: 'b2b_self_test_pain_duration_option_over_12_weeks',
          checked: false,
        },
      ]
    : [
        {
          value: 'less-than-4-weeks',
          label: 'b2b_self_test_pain_duration_option_below_4_weeks',
          checked: false,
        },
        {
          value: '4-to-12-weeks',
          label: 'b2b_self_test_pain_duration_option_4_to_12_weeks',
          checked: false,
        },
        {
          value: '12-to-24-weeks',
          label: 'b2b_self_test_pain_duration_option_3_to_6_months',
          checked: false,
        },
        {
          value: 'more-than-24-weeks',
          label: 'b2b_self_test_pain_duration_option_over_24_weeks',
          checked: false,
        },
      ]

defineProps({
  progress: {
    type: Number,
    default: null,
  },
  handleNextEvent: {
    type: Function,
    default: () => {},
  },
})

const emit = defineEmits(['back'])

const selfTestStore = useSelfTestStore()

const chronificationLevels = computed(() => selfTestStore.chronificationLevels)

const handleChronificationLevelChange = (chronificationLevel) => {
  selfTestStore.updateChronificationLevels(chronificationLevel)
}

if (!chronificationLevels.value.length) {
  selfTestStore.chronificationLevels = getDefaultChronificationLevels()
}

const isValid = computed(() =>
  chronificationLevels.value.some((l) => l.checked),
)
</script>

<style lang="scss">
@use '@shared/modules/assessment/views/css/SelfTest.scss' as *;
</style>
